"use client";

import Image from "next/image";
import Link from "next/link";
import { Phone, ArrowRight, Shield, AlertTriangle, Clock, FileCheck } from "lucide-react";
import { AuthorityRefs } from "@/components/seo/authority-refs";

const F = { display: "'Plus Jakarta Sans', sans-serif", body: '"Barlow", sans-serif', mono: '"DM Mono", monospace' };
const C = { gold: "#D4A84B", white: "#ffffff", w82: "rgba(255,255,255,0.82)", w55: "rgba(255,255,255,0.70)", w30: "rgba(255,255,255,0.30)", navy: "#080F1A", navyMid: "#0d1929" };
const CARD: React.CSSProperties = { background: "rgba(5,13,24,0.72)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)", border: "1px solid rgba(255,255,255,0.15)", borderTop: `2px solid ${C.gold}`, borderRadius: 14, padding: "2rem", display: "flex", flexDirection: "column" };
const PANEL: React.CSSProperties = { background: "rgba(5,13,24,0.76)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 16, padding: "2.5rem" };
const LABEL: React.CSSProperties = { color: C.gold, fontFamily: F.mono, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", display: "block", marginBottom: "0.6rem" };
const H2: React.CSSProperties = { color: C.white, fontFamily: F.display, fontWeight: 700, fontSize: "clamp(1.8rem, 3.5vw, 3rem)", marginBottom: "0.75rem", lineHeight: 1.1 };
const H3: React.CSSProperties = { color: C.white, fontFamily: F.display, fontWeight: 700, fontSize: "1.3rem", marginBottom: "0.75rem" };
const P: React.CSSProperties = { color: C.w82, fontSize: "0.95rem", lineHeight: 1.75, fontFamily: F.body };
const OVERLAY = (o: number): React.CSSProperties => ({ position: "absolute", inset: 0, background: `rgba(5,13,24,${o})`, zIndex: 1 });
const CONTENT: React.CSSProperties = { position: "relative", zIndex: 2, padding: "5rem 1.5rem", maxWidth: 1200, margin: "0 auto", width: "100%" };
const SEC: React.CSSProperties = { position: "relative", overflow: "hidden", backgroundColor: C.navy };
const GOLDLINE: React.CSSProperties = { width: 48, height: 2, background: C.gold, borderRadius: 2, margin: "16px 0" };
const BTN_RED: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 10, background: "linear-gradient(135deg, #DC2626 0%, #dc2626 100%)", color: C.white, fontFamily: F.display, fontWeight: 700, fontSize: "1.1rem", borderRadius: 12, padding: "16px 36px", textDecoration: "none", border: "none", letterSpacing: "1px", boxShadow: "0 8px 32px rgba(220,38,38,0.55), 0 2px 8px rgba(220,38,38,0.4)" };
const BTN_GOLD: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 8, background: "transparent", color: C.gold, fontFamily: F.body, fontWeight: 600, fontSize: "1rem", border: `1.5px solid ${C.gold}`, borderRadius: 10, padding: "13px 26px", textDecoration: "none" };

const fears = [
  {
    icon: AlertTriangle,
    title: "Patient Safety Risk",
    fear: "A water intrusion or fire event in a patient-care area introduces airborne particulates, mold spore risk, and contamination pathways that threaten immunocompromised patients, surgical suites, and sterile processing.",
    answer: "DRR deploys ICRA 2.0 Class III–V containment from the first hour. Negative air machines, HEPA filtration, zippered barriers, and anteroom construction are standard — not upgrades. Our teams include certified Industrial Hygienists who monitor air quality throughout the project.",
  },
  {
    icon: FileCheck,
    title: "Regulatory & Accreditation Exposure",
    fear: "A poorly managed restoration triggers Joint Commission citations, CMS survey findings, or state health department scrutiny. Documentation gaps become compliance gaps — and compliance gaps become operational shutdowns.",
    answer: "Every DRR healthcare project produces a complete compliance package: daily barrier inspection logs, air monitoring data, PCRA documentation, and close-out packages built for Joint Commission and CMS review. We document to defend — not just to record.",
  },
  {
    icon: Clock,
    title: "Operational Continuity",
    fear: "Your facility never closes. Shutting down a wing, diverting patients, or canceling procedures costs hundreds of thousands per day and damages community trust that took years to build.",
    answer: "We work around your patients, not the other way around. Phased scheduling, off-peak-hour execution, daily coordination with Facilities and EVS, and real-time project manager updates ensure your operations continue uninterrupted. From day one to the day the project is completed.",
  },
];

const creds = [
  { label: "ICRA 2.0 Class I–V", icon: Shield },
  { label: "PCRA Certified", icon: Shield },
  { label: "HAZWOPER 40-Hr", icon: Shield },
  { label: "IICRC Certified Firm", icon: Shield },
  { label: "Joint Commission Ready", icon: FileCheck },
];

export default function HealthcarePage() {
  return (
    <>
      {/* ═══ HERO ═══ */}
      <section style={{ ...SEC, minHeight: "90vh", display: "flex", alignItems: "center" }}>
        <Image src="/images/arizona-landmarks/saguaro-cactus-desert-arizona.jpg" alt="Arizona landscape" fill style={{ objectFit: "cover" }} priority />
        <div style={OVERLAY(0.55)} />
        <div style={{ ...CONTENT, maxWidth: 900, textAlign: "center" }}>
          <span style={LABEL}>Healthcare Facility Restoration</span>
          <h1 style={{ color: C.white, fontFamily: F.display, fontWeight: 800, fontSize: "clamp(2.2rem, 5vw, 4rem)", lineHeight: 1.05, marginBottom: 20 }}>
            Healthcare Facilities Never Close.<br />Neither Do We.
          </h1>
          <div style={{ ...GOLDLINE, margin: "20px auto" }} />
          <p style={{ ...P, fontSize: "1.1rem", maxWidth: 640, margin: "0 auto 16px" }}>
            ICRA 2.0 Class III–V certified. PCRA compliant. Negative air and HEPA filtration as standard.
            Active across 6 states. References available upon request.
          </p>
          <p style={{ color: C.gold, fontFamily: F.display, fontWeight: 600, fontSize: "1rem", marginBottom: 36 }}>
            Quality is knowing what to do the minute you step foot on the building.
          </p>
          <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
            <Link href="/contact" style={BTN_GOLD}>
              Request Vendor Consultation <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ PAIN SECTION — 3 FEARS ═══ */}
      <section style={{ ...SEC, backgroundColor: C.navyMid }}>
        <div style={CONTENT}>
          <div style={{ textAlign: "center", marginBottom: "3rem" }}>
            <span style={LABEL}>The Stakes</span>
            <h2 style={H2}>What Keeps Healthcare Facility Managers Up at Night</h2>
            <div style={{ ...GOLDLINE, margin: "16px auto" }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "1.5rem" }}>
            {fears.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} style={CARD}>
                  <Icon size={28} style={{ color: C.gold, marginBottom: 16 }} />
                  <h3 style={H3}>{f.title}</h3>
                  <p style={{ ...P, fontSize: "0.88rem", marginBottom: 20 }}>
                    <span style={{ color: "rgba(220,38,38,0.9)", fontWeight: 600 }}>The fear: </span>
                    {f.fear}
                  </p>
                  <div style={{ background: "rgba(212,168,75,0.08)", border: "1px solid rgba(212,168,75,0.20)", borderRadius: 10, padding: "1rem", marginTop: "auto" }}>
                    <p style={{ ...P, fontSize: "0.88rem" }}>
                      <span style={{ color: C.gold, fontWeight: 600 }}>How DRR solves it: </span>
                      {f.answer}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ CREDENTIALS STRIP ═══ */}
      <section style={{ ...SEC, padding: "60px 24px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "2rem" }}>
            <span style={LABEL}>Healthcare Credentials</span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", justifyContent: "center" }}>
            {creds.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.label} style={{ background: "rgba(5,13,24,0.60)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.14)", borderTop: `2px solid ${C.gold}`, borderRadius: 10, padding: "0.9rem 1.25rem", display: "flex", alignItems: "center", gap: 8 }}>
                  <Icon size={16} style={{ color: C.gold, flexShrink: 0 }} />
                  <span style={{ color: C.white, fontFamily: F.body, fontWeight: 600, fontSize: "0.82rem" }}>{c.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ CASE STUDY TEASER ═══ */}
      <section style={{ ...SEC, minHeight: "50vh" }}>
        <Image src="/images/arizona-landmarks/sedona-cathedral-rock-arizona.jpg" alt="Arizona red rocks" fill style={{ objectFit: "cover" }} />
        <div style={OVERLAY(0.60)} />
        <div style={{ ...CONTENT, maxWidth: 800 }}>
          <div style={{ ...PANEL, padding: "3rem" }}>
            <span style={{ ...LABEL, marginBottom: 16 }}>Case Study</span>
            <h2 style={{ ...H2, fontSize: "clamp(1.5rem, 3vw, 2.2rem)" }}>
              Healthcare Facility — Central Arizona
            </h2>
            <p style={{ ...P, marginBottom: 20 }}>
              Pipe burst caused water intrusion across multiple patient-care areas with secondary mold growth.
              DRR mobilized within hours, establishing full ICRA Class IV containment in an active facility.
              Negative air machines, HEPA filtration, zippered barriers, and daily air monitoring maintained
              throughout the project. Sterile Processing Department compliance maintained at all times.
              Completed on schedule with full regulatory documentation package.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 20 }}>
              {["ICRA Class IV", "Water + Mold", "Active Patient Care", "Full Compliance"].map((tag) => (
                <span key={tag} style={{ background: "rgba(212,168,75,0.12)", border: "1px solid rgba(212,168,75,0.25)", borderRadius: 20, padding: "4px 14px", fontFamily: F.mono, fontSize: "9px", letterSpacing: "1.5px", textTransform: "uppercase", color: C.gold }}>
                  {tag}
                </span>
              ))}
            </div>
            <p style={{ fontSize: "0.78rem", fontStyle: "italic", color: C.gold, opacity: 0.8 }}>
              No patient information disclosed. No facility manager names. References available upon request.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ FACILITY TYPES ═══ */}
      <section style={{ ...SEC, backgroundColor: C.navyMid, padding: "80px 24px" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto", textAlign: "center" }}>
          <span style={LABEL}>Facilities We Serve</span>
          <h2 style={{ ...H2, marginBottom: 24 }}>Every Healthcare Environment</h2>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center" }}>
            {[
              "Acute Care Hospitals", "Surgery Centers", "Outpatient Clinics",
              "Medical Office Buildings", "Behavioral Health", "Long-Term Care / SNF",
              "Rehabilitation Centers", "Dialysis Centers", "Imaging & Radiology",
              "Laboratory Facilities", "Cancer Treatment Centers",
            ].map((type) => (
              <span key={type} style={{ background: "rgba(5,13,24,0.65)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 20, padding: "6px 16px", fontFamily: F.body, fontSize: "0.82rem", color: C.w82 }}>
                {type}
              </span>
            ))}
          </div>
          <p style={{ marginTop: 24, fontSize: "0.82rem", color: C.w55 }}>
            6 states. References available upon request.
          </p>
        </div>
      </section>

      <AuthorityRefs
        heading="Healthcare-specific deep references"
        refs={[
          { href: "/authority/icra-2-protocols", tag: "ASHE / ICRA 2.0", label: "Class III–V Containment Protocols" },
          { href: "/authority/mold-conditions", tag: "IICRC S520", label: "Mold Condition 1–3 Reference" },
          { href: "/authority/water-damage-categories", tag: "IICRC S500", label: "Water Damage Categories" },
        ]}
      />

      {/* ═══ EMERGENCY CTA ═══ */}
      <section style={{ ...SEC, minHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Image src="/images/arizona-landmarks/monument-valley-night-arizona.jpg" alt="Arizona night sky" fill style={{ objectFit: "cover" }} />
        <div style={OVERLAY(0.55)} />
        <div style={{ ...CONTENT, maxWidth: 750, textAlign: "center" }}>
          <div style={{ ...PANEL, padding: "56px 40px" }}>
            <span style={{ ...LABEL, marginBottom: 20 }}>AVAILABLE 24 · 7 · 365</span>
            <h2 style={{ color: C.white, fontFamily: F.display, fontWeight: 800, fontSize: "clamp(1.8rem, 4vw, 3rem)", lineHeight: 1.08, marginBottom: 16 }}>
              We work around your patients,<br />not the other way around.
            </h2>
            <p style={{ ...P, fontSize: "1rem", marginBottom: 36, maxWidth: 520, margin: "0 auto 36px" }}>
              From day one to the day the project is completed. ICRA 2.0 certified crews dispatched with the right containment, equipment, and documentation from the first hour on-site.
            </p>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
              <Link href="/contact" style={BTN_GOLD}>
                Request Vendor Consultation <ArrowRight size={16} />
              </Link>
            </div>
            <p style={{ marginTop: 28, fontFamily: F.mono, fontSize: "0.62rem", letterSpacing: "2px", textTransform: "uppercase", color: C.w30 }}>
              AZ ROC #349012 · KB-1 Dual License · ICRA 2.0 · HAZWOPER
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
